import { createFileRoute, useParams, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/src/components/AuthContextProvider'
import { UpsertTemplateForm } from '@/src/components/manage/mutate/upsertTemplate'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { getTemplateById, upsertTemplate } from '@/src/api/template'
import { StatusSwitch } from '@/src/components/ui/status-switch'
import { toast } from 'sonner'
import { PERMISSIONS } from '@/permission.config'

export const Route = createFileRoute('/_manage/manage/templates/$id')({
  component: TemplateEditPage,
})

function TemplateEditPage() {
  const { id } = useParams({ from: '/_manage/manage/templates/$id' })
  const isNew = id === 'new'
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { permissions, isAdmin } = useAuth()
  
  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.TEMPLATE_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])

  const { data: template, isLoading, error } = useQuery({
    queryKey: ['template', 'get', id],
    queryFn: () => getTemplateById({ data: { id } }),
    enabled: !isNew,
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!template) return
      await upsertTemplate({
        data: {
          ...template,
          enabled,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template', 'get', id] })
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      toast.success('Status updated')
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update status')
    },
  })

  const handleEnabledChange = (enabled: boolean) => {
    if (isNew) return
    toggleEnabledMutation.mutate(enabled)
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            {isNew ? 'Create New Template' : 'Edit Template'}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          {!isNew && (
            <StatusSwitch
              id="template-enabled"
              label="Enabled"
              checked={template?.enabled ?? true}
              onCheckedChange={handleEnabledChange}
              disabled={isLoading || toggleEnabledMutation.isPending}
            />
          )}
        </AdminToolbar.Right>
      </AdminToolbar>
      <UpsertTemplateForm
        id={id}
        isNew={isNew}
        template={template}
        isLoading={isLoading}
        callbackUrl="/manage/templates"
      />
      {error && <div className="text-destructive mt-2">{error instanceof Error ? error.message : 'Failed to load template.'}</div>}
    </div>
  )
}
